(function () {
  // Gate Vera's product_seen_chat (GCS widget ProductCard IntersectionObserver).
  // The widget fires as soon as a .vc-product is 50% in view — including the
  // collapsed inline teaser, panel open/close, and remounts on a new message.
  // Desired: only while .vi-block.vi-expanded, never on first collapsed render,
  // once per product (keyed on brand+title) per page load. Visibility is decided
  // by this file's own observer, never by a measured rect — see observeCards.
  if (window.__veraProductSeenChatInit) return;
  window.__veraProductSeenChatInit = true;

  var seenHandles = {};
  var emittedCards = typeof WeakSet === 'function' ? new WeakSet() : null;
  var catalog = {};
  var passThrough = false;
  var cardObserver = null;
  var treeObserver = null;
  var observedBlock = null;
  var wrapTimer = null;
  var wrapAttempts = 0;
  var originalGtag = null;
  var originalAmplitudeTrack = null;

  function debug() {
    try {
      return window.localStorage && localStorage.getItem('vera_product_seen_chat_debug') === '1';
    } catch (e) {
      return false;
    }
  }

  function log(message, extra) {
    if (!debug()) return;
    if (typeof extra === 'undefined') {
      console.log('[product_seen_chat]', message);
    } else {
      console.log('[product_seen_chat]', message, extra);
    }
  }

  function isInlineExpanded() {
    return !!(document.querySelector && document.querySelector('.vi-block.vi-expanded'));
  }

  function isChatPanelOpen() {
    return !!(document.body && document.body.classList.contains('vc-open')) ||
      !!(document.querySelector && document.querySelector('.vc-box.open'));
  }

  function catalogKey(brand, title) {
    return String(brand || '').trim() + '\0' + String(title || '').trim();
  }

  function remember(props) {
    if (!props) return;
    var key = catalogKey(props.brand, props.title);
    // Never store under the empty key. A product with neither brand nor title
    // cannot be matched back to a card by text, and parking it there made it the
    // answer for every card whose own brand/title had not rendered yet — one
    // event for a product that is nowhere on screen.
    if (key === '\0') return;
    catalog[key] = {
      handle: props.handle,
      title: props.title,
      brand: props.brand
    };
  }

  // One key for both emit paths. Keying on the handle when it happens to be known
  // and on brand+title when it is not gave the same product two keys, so the
  // widget's intercept and the theme's own scan each emitted once — 2 events per
  // product. Brand+title is what both paths always have; handle is payload, and
  // only a fallback key for a card with no text.
  function identity(props) {
    if (!props) return '';
    var key = catalogKey(props.brand, props.title);
    if (key !== '\0') return key;
    return props.handle ? 'h:' + props.handle : '';
  }

  function propsFromCard(el) {
    if (!el || !el.querySelector) return null;
    var brandEl = el.querySelector('.vc-product-brand');
    var titleEl = el.querySelector('.vc-product-title');
    var brand = brandEl && brandEl.textContent ? brandEl.textContent.trim() : '';
    var title = titleEl && titleEl.textContent ? titleEl.textContent.trim() : '';
    // Nothing to match on yet; let the widget's own announcement carry this card.
    if (!brand && !title) return null;
    var remembered = catalog[catalogKey(brand, title)];
    if (remembered) return remembered;
    return { handle: '', title: title, brand: brand };
  }

  function emit(props) {
    var id = identity(props);
    if (!id) {
      log('skip: no identity', props);
      return;
    }
    if (seenHandles[id]) {
      log('skip: already seen', id);
      return;
    }
    if (!isInlineExpanded()) {
      log('skip: inline not expanded', id);
      return;
    }
    seenHandles[id] = 1;
    var payload = {
      handle: props.handle || '',
      title: props.title || '',
      brand: props.brand || ''
    };
    log('emit', payload);
    passThrough = true;
    try {
      if (typeof originalGtag === 'function') {
        originalGtag('event', 'product_seen_chat', payload);
      } else if (typeof window.gtag === 'function' && !window.gtag.__veraProductSeenChatWrapped) {
        window.gtag('event', 'product_seen_chat', payload);
      }
      var ampTrack = originalAmplitudeTrack;
      if (typeof ampTrack !== 'function' && window.amplitude && typeof window.amplitude.track === 'function' && !window.amplitude.track.__veraProductSeenChatWrapped) {
        ampTrack = window.amplitude.track.bind(window.amplitude);
      }
      if (typeof ampTrack === 'function') {
        ampTrack('product_seen_chat', payload);
      }
    } finally {
      passThrough = false;
    }
  }

  function markEmitted(el) {
    if (emittedCards) emittedCards.add(el);
    else el.__veraProductSeenChatEmitted = 1;
  }

  function hasEmitted(el) {
    return emittedCards ? emittedCards.has(el) : !!el.__veraProductSeenChatEmitted;
  }

  // An unknown handle means the widget has not announced this card yet, and it
  // always does (trackProductSeenChat carries product.handle) — so let its own
  // intercept emit rather than sending a blank handle from here.
  function emitFromCard(el, retry) {
    if (hasEmitted(el)) return;
    var props = propsFromCard(el);
    if (props && props.handle) {
      markEmitted(el);
      emit(props);
      return;
    }
    if (!retry) {
      requestAnimationFrame(function () {
        emitFromCard(el, true);
      });
    }
  }

  function interceptWidgetSeen(props) {
    remember(props);
    log('widget intercepted', props);
    // Widget still owns visibility for cards whose observer was set up while
    // expanded (new message, previously hidden grid cells). Honour those only
    // when the inline block is expanded; collapsed teaser / panel are dropped.
    if (!isInlineExpanded() || isChatPanelOpen()) return;
    emit(props);
  }

  function eventNameFromGtagArgs(args) {
    if (!args || args.length < 2) return '';
    if (args[0] === 'event') return args[1];
    return '';
  }

  // `inline_block_shown` with mode "search" means Vera rendered an answer on a search: the shopping_vera
  // treatment exposure (assets/ab_test_villoid_widget.js). It is caught here because this is the one place
  // that already wraps the widget's tracking calls, and wrapping them twice would stack.
  function notifyExposure(name, props) {
    if (name === 'inline_block_shown' && props && props.mode === 'search' &&
        typeof window.villoidFireVeraExposure === 'function') {
      window.villoidFireVeraExposure();
    }
  }

  function wrapGtag() {
    var current = window.gtag;
    if (typeof current !== 'function' || current.__veraProductSeenChatWrapped) return;
    originalGtag = current;
    function wrapped() {
      var name = eventNameFromGtagArgs(arguments);
      notifyExposure(name, arguments[2]);
      if (name === 'product_seen_chat' && !passThrough) {
        interceptWidgetSeen(arguments[2] || {});
        return;
      }
      if (name === 'inline_block_expanded') {
        var expandedResult = originalGtag.apply(this, arguments);
        requestAnimationFrame(syncExpandedObserver);
        return expandedResult;
      }
      if (name === 'inline_block_collapsed') {
        var collapsedResult = originalGtag.apply(this, arguments);
        stopCardObserver();
        return collapsedResult;
      }
      return originalGtag.apply(this, arguments);
    }
    wrapped.__veraProductSeenChatWrapped = true;
    window.gtag = wrapped;
  }

  function wrapAmplitude() {
    if (!window.amplitude || typeof window.amplitude.track !== 'function') return;
    if (window.amplitude.track.__veraProductSeenChatWrapped) return;
    originalAmplitudeTrack = window.amplitude.track.bind(window.amplitude);
    function wrapped(eventName, props) {
      notifyExposure(eventName, props);
      if (eventName === 'product_seen_chat' && !passThrough) {
        interceptWidgetSeen(props || {});
        return;
      }
      if (eventName === 'inline_block_expanded') {
        var expandedResult = originalAmplitudeTrack(eventName, props);
        requestAnimationFrame(syncExpandedObserver);
        return expandedResult;
      }
      if (eventName === 'inline_block_collapsed') {
        var collapsedResult = originalAmplitudeTrack(eventName, props);
        stopCardObserver();
        return collapsedResult;
      }
      return originalAmplitudeTrack(eventName, props);
    }
    wrapped.__veraProductSeenChatWrapped = true;
    window.amplitude.track = wrapped;
  }

  function stopCardObserver() {
    if (cardObserver) {
      cardObserver.disconnect();
      cardObserver = null;
    }
    observedBlock = null;
  }

  // The IntersectionObserver is the only visibility authority: it clips against
  // ancestor overflow, so a card parked outside the product row does not count,
  // and it reports already-visible targets on its first cycle. A getBoundingClientRect
  // sweep measured against the window alone counted the next card in the row.
  // Re-observing a card the observer already unobserved re-fires its callback, so
  // appending a row ("Se flere") replayed every card above it. Skip what is done.
  function observeCards(block) {
    if (!cardObserver || !block) return;
    var cards = block.querySelectorAll('.vc-product');
    for (var i = 0; i < cards.length; i++) {
      if (!hasEmitted(cards[i])) cardObserver.observe(cards[i]);
    }
  }

  function syncExpandedObserver() {
    wrapGtag();
    wrapAmplitude();
    if (!isInlineExpanded()) {
      stopCardObserver();
      return;
    }
    var block = document.querySelector('.vi-block.vi-expanded');
    if (!block) return;
    if (cardObserver && observedBlock === block) {
      observeCards(block);
      return;
    }
    stopCardObserver();
    observedBlock = block;
    cardObserver = new IntersectionObserver(function (entries) {
      if (!isInlineExpanded()) return;
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        emitFromCard(entries[i].target);
        if (cardObserver) cardObserver.unobserve(entries[i].target);
      }
    }, { threshold: 0.5 });
    observeCards(block);
    log('observer attached');
  }

  var watchedBlocks = typeof WeakSet === 'function' ? new WeakSet() : null;

  function watchBlock(block) {
    if (!block) return;
    if (watchedBlocks) {
      if (watchedBlocks.has(block)) return;
      watchedBlocks.add(block);
    } else if (block.__veraProductSeenChatWatched) {
      return;
    } else {
      block.__veraProductSeenChatWatched = true;
    }
    var blockObserver = new MutationObserver(function () {
      syncExpandedObserver();
    });
    blockObserver.observe(block, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });
    syncExpandedObserver();
  }

  function ensureTreeObserver() {
    if (treeObserver || !document.documentElement) return;
    treeObserver = new MutationObserver(function () {
      var block = document.querySelector('.vi-block');
      if (block) watchBlock(block);
    });
    treeObserver.observe(document.documentElement, { childList: true, subtree: true });
    var existing = document.querySelector('.vi-block');
    if (existing) watchBlock(existing);
  }

  function onToggleClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('.vi-toggle, .vi-open-hit, .vi-close')) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(syncExpandedObserver);
    });
  }

  function installWraps() {
    wrapGtag();
    wrapAmplitude();
    wrapAttempts += 1;
    if (wrapAttempts >= 80 && wrapTimer) {
      clearInterval(wrapTimer);
      wrapTimer = null;
    }
  }

  function start() {
    installWraps();
    wrapTimer = setInterval(installWraps, 100);
    ensureTreeObserver();
    document.addEventListener('click', onToggleClick, true);
    syncExpandedObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
